import { sum } from 'lodash';

import { updateChart } from 'utils/form';
import {
  sankeyFormFlowsArrayToMap,
  sankeyFormFlowsMapToArray,
} from 'utils/sankey';

import { newFlowId } from '../../../../diagram/flows';
import NodeMoves from '../../../../diagram/nodeMoves';
import { SankeyNode } from '../../../../diagram/types';
import { TeamSettings } from '../../../../utils/team';
import { getNodeNamesFromFlows } from '../../../DataEditor/utils';
import { getNodeByName, updateNodesFromFlows } from '../../utils';

// Outputs a unique name for a new node based on the base name
// e.g. "Node" -> "Node (copy)", "Node (copy 2)"...
const generateNodeCopyName = (form: SankeyForm, baseName: string) => {
  let copyName = `${baseName} (copy)`;
  let index = 1;
  while (getNodeByName(form, copyName)) {
    index += 1;
    copyName = `${baseName} (copy ${index})`;
  }
  return copyName;
};

// Outputs the optimal amount of flow to add to the left and right of a node based on the existing flows:
//   1. Fill up the side with remaining space if one side has more space than the other
//   2. Otherwise, fill up the side with the smallest flow amount from that side
const getOptimalNewNodeAmount = (form: SankeyForm, nodeName: string) => {
  const flowsArray = sankeyFormFlowsMapToArray(form.getValues('flows'));
  const leftAmounts = flowsArray
    .filter((flow) => flow.target === nodeName)
    .map((flow) => Number(flow.amount));
  const rightAmounts = flowsArray
    .filter((flow) => flow.source === nodeName)
    .map((flow) => Number(flow.amount));

  return {
    left:
      sum(rightAmounts) > sum(leftAmounts)
        ? sum(rightAmounts) - sum(leftAmounts)
        : Math.min(...leftAmounts),
    right:
      sum(leftAmounts) > sum(rightAmounts)
        ? sum(leftAmounts) - sum(rightAmounts)
        : Math.min(...rightAmounts),
  };
};

const isEmptyFlow = (flow: SankeyFormFlow) => !flow.source && !flow.target;

// Builds a new flow object based on the nodes + amount form data and places it in the first empty data grid row
const buildNewFlow = (
  form: SankeyForm,
  data: Pick<SankeyFormFlow, 'source' | 'target' | 'amount'>
): SankeyFormFlow => {
  const flowsArray = sankeyFormFlowsMapToArray(form.getValues('flows'));
  const firstEmptyRowIndex = flowsArray.find(isEmptyFlow)?.dataGridRowIndex;
  const dataGridRowIndex = firstEmptyRowIndex ?? flowsArray.length;
  return {
    id: newFlowId(dataGridRowIndex),
    amountComparison: NaN,
    dataGridRowIndex,
    ...data,
  };
};

// TODO: Unit test this method
export const addFlowToLeftOfNode = (
  form: SankeyForm,
  targetNodeName: string,
  teamSettings: TeamSettings
) => {
  const newFlow = buildNewFlow(form, {
    target: targetNodeName,
    source: generateNodeCopyName(form, targetNodeName),
    amount: getOptimalNewNodeAmount(form, targetNodeName).left,
  });
  form.setValue(
    'flows',
    {
      ...form.getValues('flows'),
      [newFlow.id]: newFlow,
    },
    { shouldDirty: true }
  );
  updateNodesFromFlows(form, teamSettings);
};

// TODO: Unit test this method
export const addFlowToRightOfNode = (
  form: SankeyForm,
  sourceNodeName: string,
  teamSettings: TeamSettings
) => {
  const newFlow = buildNewFlow(form, {
    source: sourceNodeName,
    target: generateNodeCopyName(form, sourceNodeName),
    amount: getOptimalNewNodeAmount(form, sourceNodeName).right,
  });
  form.setValue(
    'flows',
    {
      ...form.getValues('flows'),
      [newFlow.id]: newFlow,
    },
    { shouldDirty: true }
  );
  updateNodesFromFlows(form, teamSettings);
};

// TODO: Unit test this method
export const removeNode = (
  form: SankeyForm,
  nodeName: string,
  teamSettings: TeamSettings
) => {
  // Remove all flows connected to the node and re-index the rest of them
  form.setValue(
    'flows',
    sankeyFormFlowsArrayToMap(
      sankeyFormFlowsMapToArray(form.getValues('flows'))
        .filter((flow) => flow.source !== nodeName && flow.target !== nodeName)
        .map((flow, dataGridRowIndex) => ({
          ...flow,
          id: newFlowId(dataGridRowIndex),
          dataGridRowIndex,
        }))
    ),
    { shouldDirty: true }
  );
  // Finally remove the node itself
  updateNodesFromFlows(form, teamSettings);
};

// TODO: Unit test this method
export const renameNode = (
  form: SankeyForm,
  nodeId: string,
  newName: string
) => {
  const oldName = form.getValues(`nodes.${nodeId}.name`);
  form.setValue(
    'flows',
    sankeyFormFlowsArrayToMap(
      sankeyFormFlowsMapToArray(form.getValues('flows')).map((flow) => ({
        ...flow,
        source: flow.source === oldName ? newName : flow.source,
        target: flow.target === oldName ? newName : flow.target,
      }))
    ),
    { shouldDirty: true }
  );
  form.setValue(`nodes.${nodeId}.name`, newName);
  updateChart(form);
  // Update the node moves to reflect the new node name
  new NodeMoves(form).renameNode(oldName, newName);
};

// Returns the list of all the node names but the current one's, to avoid clashing names
export const useClashingNamesForNode = (form: SankeyForm, node: SankeyNode) => {
  const flows = sankeyFormFlowsMapToArray(form.watch('flows'));
  const nodeNames = getNodeNamesFromFlows(flows);
  return nodeNames.filter((nodeName) => nodeName !== node.name);
};
